import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { paysheetApi, financeSummaryApi } from "../../services/paysheet-api";
import type {
  CreatePaysheetPayload,
  FinanceActivity,
  PaymentMethod,
  PaysheetDetail,
  PaysheetStatus,
  PaysheetSummary
} from "../../types/paysheet.types";
import { adminDataApi, type ProgramDto } from "../../services/admin-data-api";
import { useAuthStore } from "../../store/auth-store";

const statusFilters: Array<{ label: string; value: PaysheetStatus | "ALL" }> = [
  { label: "All", value: "ALL" },
  { label: "Draft", value: "DRAFT" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Verified", value: "VERIFIED" },
  { label: "Authorized", value: "AUTHORIZED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" }
];

const paymentMethods: Array<{ label: string; value: PaymentMethod }> = [
  { label: "Bank Transfer", value: "BANK_TRANSFER" },
  { label: "Co-operative Bank Cash", value: "CO_OP_CASH" },
  { label: "DRO Cash", value: "DRO_CASH" }
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const emptyLineItem = () => ({
  participantName: "",
  amount: 0,
  paymentMethod: "BANK_TRANSFER" as PaymentMethod,
  nationalId: "",
  bankAccountNumber: "",
  bankRoutingNumber: "",
  cooperativeAccountRef: "",
  districtCode: "",
  notes: ""
});

type Role = "ADMIN" | "CASE_MANAGER" | "CITIZEN" | "TREASURY_ACCOUNT_CLERK" | "TREASURY_OFFICER" | "AUTHORIZING_OFFICER" | "AUDITOR";

export const PaysheetsPage = () => {
  const session = useAuthStore((state) => state.session);
  const role = (session?.user.role as Role) ?? "CITIZEN";

  const [paysheets, setPaysheets] = useState<PaysheetSummary[]>([]);
  const [financeSummary, setFinanceSummary] = useState({
    paysheetStatusBreakdown: [] as Array<{ status: PaysheetStatus; count: number }>,
    paysheetAuthorizedTotal: 0,
    paysheetPendingApproval: 0,
    recentFinanceActivities: [] as FinanceActivity[]
  });
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [filters, setFilters] = useState<{ status: PaysheetStatus | "ALL"; query: string }>({
    status: "ALL",
    query: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [detail, setDetail] = useState<PaysheetDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionComment, setActionComment] = useState("");
  const [feedback, setFeedback] = useState<{ message: string; severity: "success" | "error" } | null>(
    null
  );

  useEffect(() => {
    void fetchPrograms();
  }, []);

  useEffect(() => {
    void refreshData();
  }, [filters.status]);

  const refreshData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [list, summary] = await Promise.all([
        paysheetApi.list({ status: filters.status === "ALL" ? undefined : filters.status, query: filters.query }),
        financeSummaryApi.get()
      ]);
      setPaysheets(list);
      setFinanceSummary(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load paysheets");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const data = await adminDataApi.getPrograms();
      setPrograms(data);
    } catch (err) {
      console.error("Failed to load programs", err);
    }
  };

  const handleViewPaysheet = async (id: string) => {
    setDetailLoading(true);
    setActionComment("");
    try {
      const data = await paysheetApi.get(id);
      setDetail(data);
      setIsDetailOpen(true);
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to load paysheet details",
        severity: "error"
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async (payload: CreatePaysheetPayload) => {
    try {
      const created = await paysheetApi.create(payload);
      // Close create modal first
      setIsCreateOpen(false);
      setFeedback({ message: "Paysheet created successfully", severity: "success" });
      await refreshData();
      // Wait a bit before opening detail modal to ensure create modal closes
      setTimeout(() => {
        setDetail(created);
        setIsDetailOpen(true);
      }, 300);
    } catch (err) {
      // Keep modal open on error so user can fix issues
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to create paysheet",
        severity: "error"
      });
      throw err; // Re-throw so CreatePaysheetDialog knows there was an error
    }
  };

  const refreshDetail = async (id: string) => {
    try {
      const updated = await paysheetApi.get(id);
      setDetail(updated);
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to refresh paysheet",
        severity: "error"
      });
    }
  };

  const handleSubmitPaysheet = async () => {
    if (!detail) return;
    try {
      const updated = await paysheetApi.submit(detail.id, actionComment || undefined);
      setDetail(updated);
      setActionComment("");
      setFeedback({ message: "Paysheet submitted", severity: "success" });
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to submit paysheet",
        severity: "error"
      });
    }
  };

  const handleVerifyPaysheet = async (approve: boolean) => {
    if (!detail) return;
    if (!approve && !actionComment) {
      setFeedback({
        message: "Please provide a comment when rejecting",
        severity: "error"
      });
      return;
    }
    try {
      const updated = await paysheetApi.verify(detail.id, approve, actionComment || undefined);
      setDetail(updated);
      setActionComment("");
      setFeedback({
        message: approve ? "Paysheet verified" : "Paysheet rejected",
        severity: "success"
      });
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to process review",
        severity: "error"
      });
    }
  };

  const handleAuthorizePaysheet = async (approve: boolean) => {
    if (!detail) return;
    if (!approve && !actionComment) {
      setFeedback({
        message: "Please provide a comment when rejecting",
        severity: "error"
      });
      return;
    }
    try {
      const updated = await paysheetApi.authorize(detail.id, approve, actionComment || undefined);
      setDetail(updated);
      setActionComment("");
      setFeedback({
        message: approve ? "Paysheet authorized" : "Authorization rejected",
        severity: "success"
      });
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to authorize paysheet",
        severity: "error"
      });
    }
  };

  const handleUnauthorizePaysheet = async () => {
    if (!detail) return;
    try {
      const updated = await paysheetApi.unauthorize(detail.id, actionComment || undefined);
      setDetail(updated);
      setActionComment("");
      setFeedback({ message: "Paysheet unauthorization successful - returned to verified status", severity: "success" });
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to unauthorize paysheet",
        severity: "error"
      });
    }
  };

  const handleCancelPaysheet = async () => {
    if (!detail) return;
    try {
      const updated = await paysheetApi.cancel(detail.id, actionComment || undefined);
      setDetail(updated);
      setActionComment("");
      setFeedback({ message: "Paysheet cancelled", severity: "success" });
      await refreshData();
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : "Unable to cancel paysheet",
        severity: "error"
      });
    }
  };

  const filteredPaysheets = useMemo(() => {
    if (!filters.query) return paysheets;
    const query = filters.query.toLowerCase();
    return paysheets.filter((paysheet) =>
      [
        paysheet.referenceNumber,
        paysheet.cycleName,
        formatLabel(paysheet.status),
        paysheet.program?.name
      ]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(query))
    );
  }, [filters.query, paysheets]);

  const activeSummary = useMemo(() => {
    const breakdownMap = new Map<PaysheetStatus, number>();
    financeSummary.paysheetStatusBreakdown.forEach((item) => {
      breakdownMap.set(item.status, item.count);
    });
    return {
      authorizedTotal: financeSummary.paysheetAuthorizedTotal,
      pendingApproval: financeSummary.paysheetPendingApproval,
      draftCount: breakdownMap.get("DRAFT") ?? 0,
      submittedCount: breakdownMap.get("SUBMITTED") ?? 0,
      authorizedCount: breakdownMap.get("AUTHORIZED") ?? 0
    };
  }, [financeSummary]);

  const canSubmit = role === "TREASURY_ACCOUNT_CLERK" || role === "ADMIN";
  const canVerify = role === "TREASURY_OFFICER" || role === "ADMIN";
  const canAuthorize = role === "AUTHORIZING_OFFICER" || role === "ADMIN";
  const canCancel = [
    "TREASURY_ACCOUNT_CLERK",
    "TREASURY_OFFICER",
    "AUTHORIZING_OFFICER",
    "ADMIN"
  ].includes(role);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Paysheets</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search reference or program"
            value={filters.query}
            onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => void refreshData()} size="small">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            select
            size="small"
            value={filters.status}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, status: event.target.value as PaysheetStatus | "ALL" }))
            }
          >
            {statusFilters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateOpen(true)}
            disabled={!(canSubmit || (role as Role) === "ADMIN")}
          >
            New Paysheet
          </Button>
        </Stack>
      </Box>

      {feedback ? (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 3 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      ) : null}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Authorized Total
              </Typography>
              <Typography variant="h5">{formatCurrency(activeSummary.authorizedTotal)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Pending Approvals
              </Typography>
              <Typography variant="h5">{activeSummary.pendingApproval}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Draft Paysheets
              </Typography>
              <Typography variant="h5">{activeSummary.draftCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" variant="overline">
                Authorized Paysheets
              </Typography>
              <Typography variant="h5">{activeSummary.authorizedCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardHeader
          title="Paysheet Register"
          action={
            isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Tooltip title="Refresh">
                <IconButton onClick={() => void refreshData()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <CardContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Cycle</TableCell>
                <TableCell>Disbursement</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredPaysheets.length ? (
                filteredPaysheets.map((paysheet) => (
                  <TableRow key={paysheet.id} hover>
                    <TableCell>{paysheet.referenceNumber}</TableCell>
                    <TableCell>{paysheet.program?.name ?? "—"}</TableCell>
                    <TableCell>
                      <Chip label={formatLabel(paysheet.status)} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{paysheet.cycleName}</TableCell>
                    <TableCell>{dayjs(paysheet.disbursementDate).format("MMM DD, YYYY")}</TableCell>
                    <TableCell align="right">{formatCurrency(paysheet.totalAmount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton onClick={() => void handleViewPaysheet(paysheet.id)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No paysheets found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Paysheet Status Breakdown" />
            <CardContent>
              {financeSummary.paysheetStatusBreakdown.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financeSummary.paysheetStatusBreakdown.map((item) => (
                      <TableRow key={item.status}>
                        <TableCell>{formatLabel(item.status)}</TableCell>
                        <TableCell align="right">{item.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No paysheet data yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Recent Finance Activities" />
            <CardContent>
              {financeSummary.recentFinanceActivities.length ? (
                <Stack spacing={2}>
                  {financeSummary.recentFinanceActivities.map((activity) => (
                    <Box key={activity.id}>
                      <Typography variant="subtitle2">
                        {formatLabel(activity.type.replace("PAYSHEET_", ""))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.description ?? "Activity recorded"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(activity.createdAt).format("MMM DD, YYYY h:mm A")} • {activity.actor?.email ?? "System"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">No recent activity.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CreatePaysheetDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        programs={programs}
        onSubmit={handleCreate}
      />

      <Dialog
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Paysheet Details
          <IconButton
            onClick={() => setIsDetailOpen(false)}
            sx={{ position: "absolute", right: 16, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6">Summary</Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Reference
                    </Typography>
                    <Typography variant="subtitle1">{detail.referenceNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Program
                    </Typography>
                    <Typography variant="subtitle1">{detail.program?.name ?? "—"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Cycle
                    </Typography>
                    <Typography variant="subtitle1">{detail.cycleName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Disbursement
                    </Typography>
                    <Typography variant="subtitle1">
                      {dayjs(detail.disbursementDate).format("MMM DD, YYYY")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="subtitle1">
                      {formatCurrency(detail.totalAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="subtitle1">{detail.notes ?? "—"}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="h6">Line Items</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Beneficiary</TableCell>
                      <TableCell>National ID</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.lineItems.map((item, index) => (
                      <TableRow key={item.id ?? index}>
                        <TableCell>{item.participantName}</TableCell>
                        <TableCell>{item.nationalId ?? "—"}</TableCell>
                        <TableCell>{formatLabel(item.paymentMethod)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box>
                <Typography variant="h6">Approval Trail</Typography>
                <Stack spacing={1} mt={1}>
                    {detail.approvals.map((approval) => (
                      <Box key={approval.id}>
                        <Typography variant="subtitle2">
                          {formatLabel(approval.stage)} — {formatLabel(approval.status)}
                        </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {approval.actedAt ? dayjs(approval.actedAt).format("MMM DD, YYYY h:mm A") : "Pending"}
                        {approval.comment ? ` • ${approval.comment}` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6">Activity Log</Typography>
                <Stack spacing={1} mt={1}>
                  {(detail.activities ?? []).length ? (
                    detail.activities!.map((activity) => (
                      <Box key={activity.id}>
                        <Typography variant="subtitle2">
                          {activity.description ?? (activity.type as string).replace(/_/g, " ")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(activity.createdAt).format("MMM DD, YYYY h:mm A")}
                          {activity.actor?.email ? ` • ${activity.actor.email}` : ""}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No activity recorded yet.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Typography>No paysheet selected.</Typography>
          )}
        </DialogContent>
        {detail ? (
          <DialogActions sx={{ alignItems: "flex-start", flexDirection: "column", gap: 2, p: 3 }}>
            <TextField
              label="Action comment"
              fullWidth
              value={actionComment}
              onChange={(event) => setActionComment(event.target.value)}
              multiline
              minRows={2}
            />
            <Box display="flex" gap={1} flexWrap="wrap">
              {detail.status === "DRAFT" && canSubmit ? (
                <Button variant="contained" onClick={() => void handleSubmitPaysheet()}>
                  Submit
                </Button>
              ) : null}
              {detail.status === "SUBMITTED" && canVerify ? (
                <>
                  <Button variant="contained" onClick={() => void handleVerifyPaysheet(true)}>
                    Approve
                  </Button>
                  <Button variant="outlined" color="warning" onClick={() => void handleVerifyPaysheet(false)}>
                    Request Changes
                  </Button>
                </>
              ) : null}
              {detail.status === "VERIFIED" && canAuthorize ? (
                <>
                  <Button variant="contained" color="success" onClick={() => void handleAuthorizePaysheet(true)}>
                    Authorize
                  </Button>
                  <Button variant="outlined" color="warning" onClick={() => void handleAuthorizePaysheet(false)}>
                    Reject
                  </Button>
                </>
              ) : null}
              {detail.status === "AUTHORIZED" && canAuthorize ? (
                <Button variant="outlined" color="warning" onClick={() => void handleUnauthorizePaysheet()}>
                  Unauthorize (Reverse)
                </Button>
              ) : null}
              {canCancel && ["DRAFT", "SUBMITTED", "VERIFIED"].includes(detail.status) ? (
                <Button variant="text" color="error" onClick={() => void handleCancelPaysheet()}>
                  Cancel Paysheet
                </Button>
              ) : null}
              <Button onClick={() => detail && void refreshDetail(detail.id)}>Refresh detail</Button>
            </Box>
          </DialogActions>
        ) : null}
      </Dialog>
    </Box>
  );
};

interface CreatePaysheetDialogProps {
  open: boolean;
  onClose: () => void;
  programs: ProgramDto[];
  onSubmit: (payload: CreatePaysheetPayload) => Promise<void> | void;
}

const CreatePaysheetDialog = ({ open, onClose, programs, onSubmit }: CreatePaysheetDialogProps) => {
  const [form, setForm] = useState<CreatePaysheetPayload>(() => ({
    referenceNumber: "",
    programId: programs[0]?.id ?? "",
    cycleName: "",
    disbursementDate: dayjs().format("YYYY-MM-DD"),
    notes: "",
    lineItems: [emptyLineItem()]
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({
        referenceNumber: "",
        programId: programs[0]?.id ?? "",
        cycleName: "",
        disbursementDate: dayjs().format("YYYY-MM-DD"),
        notes: "",
        lineItems: [emptyLineItem()]
      });
      setIsSubmitting(false);
    }
  }, [open, programs]);

  const updateLineItem = (index: number, key: string, value: unknown) => {
    setForm((prev) => {
      const updated = [...prev.lineItems];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, lineItems: updated };
    });
  };

  const addLineItem = () => {
    setForm((prev) => ({ ...prev, lineItems: [...prev.lineItems, emptyLineItem()] }));
  };

  const removeLineItem = (index: number) => {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, idx) => idx !== index)
    }));
  };

  const handleSubmit = async () => {
    const payload: CreatePaysheetPayload = {
      ...form,
      lineItems: form.lineItems
        .filter((item) => item.participantName.trim().length)
        .map((item) => ({
          ...item,
          amount: Number(item.amount)
        }))
    };

    if (!payload.referenceNumber.trim()) {
      return;
    }
    if (!payload.programId) {
      return;
    }
    if (!payload.lineItems.length) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      // Modal will be closed by parent component on success
    } catch (err) {
      // Error is handled by parent, just reset submitting state
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create Paysheet</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Reference Number"
            value={form.referenceNumber}
            onChange={(event) => setForm((prev) => ({ ...prev, referenceNumber: event.target.value }))}
            required
          />
          <TextField
            label="Program"
            select
            value={form.programId}
            onChange={(event) => setForm((prev) => ({ ...prev, programId: event.target.value }))}
            required
          >
            {programs.map((program) => (
              <MenuItem key={program.id} value={program.id}>
                {program.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Cycle Name"
            value={form.cycleName}
            onChange={(event) => setForm((prev) => ({ ...prev, cycleName: event.target.value }))}
            required
          />
          <TextField
            label="Disbursement Date"
            type="date"
            value={form.disbursementDate}
            onChange={(event) => setForm((prev) => ({ ...prev, disbursementDate: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Notes"
            value={form.notes ?? ""}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            multiline
            minRows={3}
          />

          <Divider textAlign="left">Line Items</Divider>
          <Stack spacing={2}>
            {form.lineItems.map((item, index) => (
              <Card key={index} variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Beneficiary Name"
                        value={item.participantName}
                        onChange={(event) => updateLineItem(index, "participantName", event.target.value)}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="National ID"
                        value={item.nationalId}
                        onChange={(event) => updateLineItem(index, "nationalId", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Amount"
                        type="number"
                        value={item.amount}
                        onChange={(event) => updateLineItem(index, "amount", Number(event.target.value))}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Method"
                        select
                        value={item.paymentMethod}
                        onChange={(event) => updateLineItem(index, "paymentMethod", event.target.value)}
                        fullWidth
                        required
                      >
                        {paymentMethods.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Bank Account"
                        value={item.bankAccountNumber}
                        onChange={(event) => updateLineItem(index, "bankAccountNumber", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Routing Number"
                        value={item.bankRoutingNumber}
                        onChange={(event) => updateLineItem(index, "bankRoutingNumber", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="DRO / District Code"
                        value={item.districtCode}
                        onChange={(event) => updateLineItem(index, "districtCode", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Line Notes"
                        value={item.notes}
                        onChange={(event) => updateLineItem(index, "notes", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  {form.lineItems.length > 1 ? (
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <Button color="error" onClick={() => removeLineItem(index)}>
                        Remove line item
                      </Button>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} onClick={addLineItem} variant="outlined">
              Add line item
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Paysheet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaysheetsPage;
